import { Avatar, Button, Dropdown } from '@heroui/react'
import { ChevronDown, CircleHelp, Eye, EyeOff, GraduationCap, KeyRound, LogOut, Moon, Search, Shield, ShieldCheck, Sun, User, Users, X } from 'lucide-react'
import { useMemo, useState, type FormEvent } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useLocation } from 'react-router'
import { useAuth } from '../../context/AuthContext'
import { useFeedback } from '../../context/FeedbackContext'
import { useActiveScope } from '../../context/ScopeContext'
import { changeOwnPassword, updateOwnProfile } from '../../services/api'

const MIN_PASSWORD_LENGTH = 10

const SCOPE_LABEL: Record<string, string> = {
  GLOBAL: 'Semua unit',
  FOUNDATION: 'Se-yayasan',
  SCHOOL_UNIT: 'Unit tertentu',
}

const MODULE_CONFIG: Record<string, { label: string }> = {
  spmb: { label: 'SPMB (Penerimaan Siswa)' },
  finance: { label: 'Keuangan & Pembayaran' },
  broadcast: { label: 'Broadcast Pesan' },
  'document-template': { label: 'Template Dokumen' },
  iam: { label: 'Akses & Identitas (IAM)' },
  access: { label: 'Akses & Identitas' },
  referral: { label: 'Program Referral' },
  cms: { label: 'CMS & Landing Page' },
  settings: { label: 'Pengaturan Sistem' },
  dashboard: { label: 'Dashboard' },
  hr: { label: 'Kepegawaian (HR)' },
  student: { label: 'Kesiswaan' },
  students: { label: 'Kesiswaan' },
  notification: { label: 'Notifikasi' },
  audit: { label: 'Log Aktivitas Panitia' },
  platform: { label: 'Platform & Infrastruktur' },
}

function getModuleLabel(key: string): string {
  if (MODULE_CONFIG[key]?.label) return MODULE_CONFIG[key].label
  return key.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function getActionBadge(code: string) {
  const parts = code.split('.')
  const action = parts[parts.length - 1]?.toLowerCase() || ''
  if (['read', 'view', 'list', 'check', 'get'].includes(action)) {
    return { label: 'Lihat', badgeClass: 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300' }
  }
  if (['write', 'create', 'update', 'edit', 'manage', 'issue', 'save'].includes(action)) {
    return { label: 'Kelola', badgeClass: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300' }
  }
  if (['delete', 'void', 'expire'].includes(action)) {
    return { label: 'Hapus', badgeClass: 'bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300' }
  }
  if (['approve', 'release', 'execute', 'reconcile', 'deliver', 'dispatch', 'retry', 'verify', 'send', 'download', 'export', 'attendance', 'roster'].includes(action)) {
    return { label: 'Aksi', badgeClass: 'bg-purple-50 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300' }
  }
  return { label: action.toUpperCase(), badgeClass: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' }
}

export function UserMenu({
  roleLabel,
  theme,
  onToggleTheme,
}: {
  roleLabel: string
  theme?: string
  onToggleTheme?: () => void
}) {
  const { user, logout, updateUser, activePersona, canSwitchPersona, switchPersona } = useAuth()
  const { availableUnits } = useActiveScope()
  const { notify } = useFeedback()
  const navigate = useNavigate()
  const location = useLocation()

  const signOut = () => {
    logout()
    navigate('/login')
  }

  const [openDialog, setOpenDialog] = useState<'password' | 'profile' | null>(null)
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' })
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [permissionSearch, setPermissionSearch] = useState('')

  // Profile form state
  const [profileName, setProfileName] = useState('')
  const [profileSaving, setProfileSaving] = useState(false)
  const [profileError, setProfileError] = useState('')
  const [profileSuccess, setProfileSuccess] = useState('')
  const [profileTab, setProfileTab] = useState<'info' | 'access'>('info')

  const groupedPermissions = useMemo(() => {
    const list = user?.permissions ?? []
    const q = permissionSearch.trim().toLowerCase()
    const filtered = q ? list.filter((p) => p.toLowerCase().includes(q)) : list

    const groups: Record<string, string[]> = {}
    for (const perm of filtered) {
      const moduleKey = perm.split('.')[0] || 'lainnya'
      if (!groups[moduleKey]) groups[moduleKey] = []
      groups[moduleKey].push(perm)
    }

    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b))
  }, [user?.permissions, permissionSearch])

  const totalFilteredCount = useMemo(() => {
    return groupedPermissions.reduce((acc, [, perms]) => acc + perms.length, 0)
  }, [groupedPermissions])

  const openPasswordDialog = () => {
    setForm({ currentPassword: '', newPassword: '', confirmPassword: '' })
    setError('')
    setSuccess('')
    setOpenDialog('password')
  }

  const openProfileDialog = () => {
    setProfileName(user?.name ?? '')
    setProfileError('')
    setProfileSuccess('')
    setProfileTab('info')
    setOpenDialog('profile')
  }

  const submitPassword = async (event: FormEvent) => {
    event.preventDefault()
    setError('')
    setSuccess('')

    if (form.newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`Kata sandi baru minimal ${MIN_PASSWORD_LENGTH} karakter.`)
      return
    }
    if (form.newPassword !== form.confirmPassword) {
      setError('Konfirmasi kata sandi tidak sama.')
      return
    }
    if (form.newPassword === form.currentPassword) {
      setError('Kata sandi baru harus berbeda dari kata sandi saat ini.')
      return
    }

    setSaving(true)
    try {
      await changeOwnPassword(form.currentPassword, form.newPassword)
      closePasswordDialog()
      notify('Kata sandi berhasil diubah.', 'success')
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : 'Kata sandi gagal diubah.')
    } finally {
      setSaving(false)
    }
  }

  const closePasswordDialog = () => {
    setOpenDialog(null)
    setShowCurrentPassword(false)
    setShowNewPassword(false)
    setShowConfirmPassword(false)
    setError('')
    setSuccess('')
    setForm({ currentPassword: '', newPassword: '', confirmPassword: '' })
  }

  const submitProfile = async (event: FormEvent) => {
    event.preventDefault()
    setProfileError('')
    setProfileSuccess('')

    const trimmed = profileName.trim()
    if (trimmed.length < 2) {
      setProfileError('Nama lengkap minimal 2 karakter.')
      return
    }

    setProfileSaving(true)
    try {
      const updated = await updateOwnProfile({ fullName: trimmed })
      updateUser({ name: updated.fullName })
      setProfileSuccess('Nama profil berhasil diperbarui.')
    } catch (cause: unknown) {
      setProfileError(cause instanceof Error ? cause.message : 'Gagal memperbarui profil.')
    } finally {
      setProfileSaving(false)
    }
  }

  const unitName = (unitId: string | null) =>
    availableUnits.find((unit) => unit.id === unitId)?.name ?? unitId ?? '-'

  return (
    <>
      <Dropdown>
        <Dropdown.Trigger data-testid="shell-user-menu-btn" className="flex items-center gap-3 rounded-lg px-1 py-1 hover:bg-slate-100 dark:hover:bg-slate-900">
          <Avatar size="sm" color="accent">
            <Avatar.Fallback>{user?.name.slice(0, 2).toUpperCase()}</Avatar.Fallback>
          </Avatar>
          <div className="hidden text-left sm:block">
            <p className="max-w-36 truncate text-sm font-black text-slate-800 dark:text-white">
              {user?.name}
            </p>
            <p className="text-xs text-slate-500">{roleLabel}</p>
          </div>
          <ChevronDown size={15} className="hidden text-slate-400 sm:block" aria-hidden />
        </Dropdown.Trigger>

        <Dropdown.Popover placement="bottom end" className="w-64">
          <div className="border-b border-slate-200 px-3 py-3 dark:border-slate-800">
            <p className="truncate text-sm font-bold text-slate-800 dark:text-white">
              {user?.name}
            </p>
            <p className="truncate text-xs text-slate-500">{user?.email}</p>
            <p className="mt-1 text-xs font-semibold text-teal-600 dark:text-teal-400">
              {roleLabel}
            </p>
          </div>

          <Dropdown.Menu
            aria-label="Menu akun"
            onAction={(key) => {
              if (key === 'switch-persona') {
                switchPersona(activePersona === 'STAFF' ? 'PARENT' : 'STAFF')
              }
              if (key === 'theme') onToggleTheme?.()
              if (key === 'help') {
                navigate(location.pathname.startsWith('/mitra') ? '/mitra/bantuan' : '/app/bantuan')
              }
              if (key === 'password') openPasswordDialog()
              if (key === 'profile') openProfileDialog()
              if (key === 'logout') signOut()
            }}
          >
            {canSwitchPersona && (
              <Dropdown.Item
                id="switch-persona"
                data-testid="shell-user-menu-switch-persona-item"
                textValue={activePersona === 'STAFF' ? 'Beralih ke Mode Orang Tua' : 'Beralih ke Mode Guru / Staf'}
              >
                <div className="flex items-center gap-2 font-medium text-teal-700 dark:text-teal-300">
                  {activePersona === 'STAFF' ? (
                    <>
                      <Users size={15} aria-hidden /> Beralih ke Mode Orang Tua
                    </>
                  ) : (
                    <>
                      <GraduationCap size={15} aria-hidden /> Beralih ke Mode Guru / Staf
                    </>
                  )}
                </div>
              </Dropdown.Item>
            )}
            {onToggleTheme && (
              <Dropdown.Item
                id="theme"
                data-testid="shell-user-menu-theme-item"
                textValue={theme === 'dark' ? 'Mode Terang' : 'Mode Gelap'}
              >
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-2">
                    {theme === 'dark' ? <Sun size={15} aria-hidden /> : <Moon size={15} aria-hidden />}
                    <span>{theme === 'dark' ? 'Mode Terang' : 'Mode Gelap'}</span>
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    {theme === 'dark' ? 'Gelap' : 'Terang'}
                  </span>
                </div>
              </Dropdown.Item>
            )}
            <Dropdown.Item
              id="help"
              data-testid="shell-user-menu-help-item"
              textValue="Pusat Bantuan"
            >
              <div className="flex items-center gap-2">
                <CircleHelp size={15} aria-hidden /> Pusat Bantuan
              </div>
            </Dropdown.Item>
            <Dropdown.Item id="password" data-testid="shell-user-menu-password-item" textValue="Ubah kata sandi">
              <div className="flex items-center gap-2">
                <KeyRound size={15} aria-hidden /> Ubah kata sandi
              </div>
            </Dropdown.Item>
            <Dropdown.Item id="profile" data-testid="shell-user-menu-profile-item" textValue="My Profile">
              <div className="flex items-center gap-2">
                <User size={15} aria-hidden /> My Profile
              </div>
            </Dropdown.Item>
            <Dropdown.Item id="logout" data-testid="shell-user-menu-logout-item" textValue="Keluar">
              <div className="flex items-center gap-2 text-rose-600 dark:text-rose-400">
                <LogOut size={15} aria-hidden /> Keluar
              </div>
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown.Popover>
      </Dropdown>

      {openDialog === 'password' &&
        createPortal(
          <div
            className="erp-modal-backdrop z-[100]"
            onMouseDown={(event) => event.currentTarget === event.target && closePasswordDialog()}
            data-testid="shell-password-modal"
          >
            <div className="erp-modal-dialog">
              <form onSubmit={submitPassword} data-testid="shell-password-form">
                <header className="erp-modal-header">
                  <h2 className="text-lg font-bold text-slate-950 dark:text-white">
                    Ubah kata sandi
                  </h2>
                  <Button
                    isIconOnly
                    variant="ghost"
                    aria-label="Tutup"
                    onPress={closePasswordDialog}
                    data-testid="shell-password-modal-close-btn"
                  >
                    <X size={18} />
                  </Button>
                </header>
                <div className="space-y-4 p-6">
                  {error && (
                    <div className="rounded-lg bg-red-50 p-3 text-xs font-bold text-red-600 dark:bg-red-950/40 dark:text-red-300">
                      {error}
                    </div>
                  )}
                  {success && (
                    <div className="rounded-lg bg-emerald-50 p-3 text-xs font-bold text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                      {success}
                    </div>
                  )}
                  <label className="block">
                    <span className="erp-form-label">Kata sandi saat ini</span>
                    <div className="relative">
                      <input
                        data-testid="shell-password-current-input"
                        className="wicend-input pr-10"
                        type={showCurrentPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        required
                        value={form.currentPassword}
                        onChange={(event) =>
                          setForm((prev) => ({ ...prev, currentPassword: event.target.value }))
                        }
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword((v) => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1.5 text-slate-400 transition-colors hover:text-slate-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-wicend-500 dark:hover:text-slate-200"
                        aria-label={showCurrentPassword ? 'Sembunyikan kata sandi' : 'Tampilkan kata sandi'}
                        data-testid="shell-password-current-visibility-btn"
                      >
                        {showCurrentPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </label>
                  <label className="block">
                    <span className="erp-form-label">
                      Kata sandi baru (minimal {MIN_PASSWORD_LENGTH} karakter)
                    </span>
                    <div className="relative">
                      <input
                        data-testid="shell-password-new-input"
                        className="wicend-input pr-10"
                        type={showNewPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        minLength={MIN_PASSWORD_LENGTH}
                        required
                        value={form.newPassword}
                        onChange={(event) =>
                          setForm((prev) => ({ ...prev, newPassword: event.target.value }))
                        }
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword((v) => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1.5 text-slate-400 transition-colors hover:text-slate-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-wicend-500 dark:hover:text-slate-200"
                        aria-label={showNewPassword ? 'Sembunyikan kata sandi' : 'Tampilkan kata sandi'}
                        data-testid="shell-password-new-visibility-btn"
                      >
                        {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </label>
                  <label className="block">
                    <span className="erp-form-label">Ulangi kata sandi baru</span>
                    <div className="relative">
                      <input
                        data-testid="shell-password-confirm-input"
                        className="wicend-input pr-10"
                        type={showConfirmPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        minLength={MIN_PASSWORD_LENGTH}
                        required
                        value={form.confirmPassword}
                        onChange={(event) =>
                          setForm((prev) => ({ ...prev, confirmPassword: event.target.value }))
                        }
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword((v) => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1.5 text-slate-400 transition-colors hover:text-slate-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-wicend-500 dark:hover:text-slate-200"
                        aria-label={showConfirmPassword ? 'Sembunyikan kata sandi' : 'Tampilkan kata sandi'}
                        data-testid="shell-password-confirm-visibility-btn"
                      >
                        {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </label>
                </div>
                <footer className="flex justify-end gap-2 border-t border-slate-200 p-4 dark:border-slate-800">
                  <Button variant="secondary" onPress={closePasswordDialog} data-testid="shell-password-modal-cancel-btn">
                    Tutup
                  </Button>
                  <Button type="submit" variant="primary" isDisabled={saving} data-testid="shell-password-modal-submit-btn">
                    {saving ? 'Menyimpan…' : 'Simpan'}
                  </Button>
                </footer>
              </form>
            </div>
          </div>,
          document.body,
        )}

      {openDialog === 'profile' &&
        createPortal(
          <div
            className="erp-modal-backdrop z-[100]"
            onMouseDown={(event) => event.currentTarget === event.target && setOpenDialog(null)}
            data-testid="shell-profile-modal"
          >
            <div className="erp-modal-dialog max-w-2xl max-h-[85vh] flex flex-col overflow-hidden shadow-2xl">
              {/* Header */}
              <header className="erp-modal-header shrink-0">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal-50 text-teal-700 dark:bg-teal-950/60 dark:text-teal-300">
                    <User size={22} />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-slate-950 dark:text-white">
                      My Profile
                    </h2>
                    <p className="text-xs text-slate-500">
                      Kelola nama profil dan informasi akun Anda
                    </p>
                  </div>
                </div>
                <Button
                  isIconOnly
                  variant="ghost"
                  aria-label="Tutup"
                  onPress={() => {
                    setOpenDialog(null)
                    setPermissionSearch('')
                  }}
                  data-testid="shell-profile-modal-close-btn"
                >
                  <X size={18} />
                </Button>
              </header>

              {/* Navigation Tabs */}
              <div className="flex border-b border-slate-200 bg-slate-50/80 px-6 pt-2 dark:border-slate-800 dark:bg-slate-900/60">
                <button
                  type="button"
                  onClick={() => setProfileTab('info')}
                  data-testid="shell-profile-info-tab"
                  className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-xs font-bold transition-colors ${
                    profileTab === 'info'
                      ? 'border-teal-600 text-teal-600 dark:border-teal-400 dark:text-teal-400'
                      : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
                  }`}
                >
                  <User size={14} /> Profil Saya
                </button>
                <button
                  type="button"
                  onClick={() => setProfileTab('access')}
                  data-testid="shell-profile-access-tab"
                  className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-xs font-bold transition-colors ${
                    profileTab === 'access'
                      ? 'border-teal-600 text-teal-600 dark:border-teal-400 dark:text-teal-400'
                      : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
                  }`}
                >
                  <ShieldCheck size={14} /> Peran &amp; Hak Akses
                  <span className="ml-1 rounded-full bg-slate-200 px-1.5 py-0.2 text-[10px] text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                    {user?.permissions?.length ?? 0}
                  </span>
                </button>
              </div>

              {/* Content Body */}
              {profileTab === 'info' ? (
                <form onSubmit={submitProfile} className="flex flex-1 flex-col overflow-y-auto">
                  <div className="flex-1 space-y-5 p-6">
                    {/* User Identity Card */}
                    <div className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900 shadow-xs">
                      <Avatar size="lg" color="accent">
                        <Avatar.Fallback className="text-base font-black">
                          {user?.name?.slice(0, 2).toUpperCase() || 'U'}
                        </Avatar.Fallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-base font-bold text-slate-900 dark:text-white">
                          {user?.name}
                        </p>
                        <p className="truncate text-xs text-slate-500">{user?.email}</p>
                        <div className="mt-1.5 flex items-center gap-2">
                          <span className="inline-flex items-center rounded-md bg-teal-50 px-2 py-0.5 text-xs font-semibold text-teal-700 dark:bg-teal-950/60 dark:text-teal-300">
                            {roleLabel}
                          </span>
                        </div>
                      </div>
                    </div>

                    {profileError && (
                      <div className="rounded-lg bg-red-50 p-3 text-xs font-bold text-red-600 dark:bg-red-950/40 dark:text-red-300">
                        {profileError}
                      </div>
                    )}
                    {profileSuccess && (
                      <div className="rounded-lg bg-emerald-50 p-3 text-xs font-bold text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                        {profileSuccess}
                      </div>
                    )}

                    <div className="space-y-4">
                      <label className="block">
                        <span className="erp-form-label">
                          Nama Lengkap <span className="text-rose-500">*</span>
                        </span>
                        <input data-testid="shell-user-menu-masukkan-name-lengkap-anda-input"
                          className="wicend-input mt-1"
                          type="text"
                          required
                          minLength={2}
                          maxLength={150}
                          value={profileName}
                          onChange={(e) => setProfileName(e.target.value)}
                          placeholder="Masukkan nama lengkap Anda"
                        />
                        <span className="mt-1 block text-[11px] text-slate-400">
                          Nama ini akan ditampilkan pada header akun dan catatan aktivitas sistem.
                        </span>
                      </label>

                      <label className="block">
                        <span className="erp-form-label">Email Akun</span>
                        <input data-testid="shell-user-menu-input-1"
                          className="wicend-input mt-1 bg-slate-100/70 text-slate-500 dark:bg-slate-900/60 dark:text-slate-400 cursor-not-allowed"
                          type="email"
                          disabled
                          value={user?.email || ''}
                        />
                        <span className="mt-1 block text-[11px] text-slate-400">
                          Email digunakan sebagai kredensial login utama dan tidak dapat diubah di sini.
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Footer */}
                  <footer className="erp-modal-footer shrink-0 flex items-center justify-end gap-2 bg-white dark:bg-slate-950 border-t border-slate-200 dark:border-slate-800 p-4">
                    <Button data-testid="shell-user-menu-open-dialog-btn"
                      variant="secondary"
                      type="button"
                      onPress={() => setOpenDialog(null)}
                    >
                      Batal
                    </Button>
                    <Button data-testid="shell-user-menu-btn-1"
                      type="submit"
                      variant="primary"
                      isDisabled={profileSaving || !profileName.trim()}
                    >
                      {profileSaving ? 'Menyimpan…' : 'Simpan Perubahan'}
                    </Button>
                  </footer>
                </form>
              ) : (
                <>
                  <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-slate-50/50 dark:bg-slate-950/40">
                    {/* Section 1: Penugasan Peran */}
                    <div>
                      <div className="flex items-center justify-between mb-2.5">
                        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                          Penugasan Peran (Role)
                        </h3>
                        <span className="text-xs text-slate-400">
                          {(user?.assignments ?? []).length} Peran
                        </span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                        {(user?.assignments ?? []).map((assignment) => (
                          <div
                            key={assignment.assignmentId}
                            className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-xs dark:border-slate-800 dark:bg-slate-900"
                          >
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-teal-50 text-teal-700 font-bold text-xs dark:bg-teal-950 dark:text-teal-300">
                              <Shield size={16} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-xs font-bold text-slate-900 dark:text-white font-mono">
                                {assignment.roleCode}
                              </p>
                              <p className="truncate text-[11px] text-slate-500">
                                {SCOPE_LABEL[assignment.scopeType] ?? assignment.scopeType}
                                {assignment.scopeType === 'SCHOOL_UNIT'
                                  ? ` · ${unitName(assignment.schoolUnitId)}`
                                  : ''}
                              </p>
                            </div>
                          </div>
                        ))}
                        {(user?.assignments?.length ?? 0) === 0 && (
                          <p className="col-span-full text-xs text-slate-500 italic">Belum ada penugasan peran.</p>
                        )}
                      </div>
                    </div>

                    {/* Section 2: Hak Akses (Grouped by Module) */}
                    <div className="space-y-3">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div>
                          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                            Rincian Hak Akses
                          </h3>
                          <p className="text-[11px] text-slate-400">
                            Izin akses yang diberikan berdasarkan penugasan peran
                          </p>
                        </div>
                        <div className="relative w-full sm:w-64">
                          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                          <input data-testid="shell-user-menu-search-hak-akses-input"
                            placeholder="Cari hak akses..."
                            value={permissionSearch}
                            onChange={(e) => setPermissionSearch(e.target.value)}
                            className="wicend-input pl-8 py-1 text-xs min-h-[34px] w-full"
                          />
                          {permissionSearch && (
                            <button data-testid="shell-user-menu-clear-pencarian-btn"
                              type="button"
                              onClick={() => setPermissionSearch('')}
                              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                              aria-label="Bersihkan pencarian"
                            >
                              <X size={12} />
                            </button>
                          )}
                        </div>
                      </div>

                      {groupedPermissions.length > 0 ? (
                        <div className="space-y-3">
                          {groupedPermissions.map(([moduleKey, perms]) => (
                            <div key={moduleKey} className="erp-card">
                              <div className="bg-slate-100/70 dark:bg-slate-800/70 px-4 py-2 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
                                <span className="font-bold text-xs uppercase tracking-wider text-slate-800 dark:text-slate-200">
                                  {getModuleLabel(moduleKey)}
                                </span>
                                <span className="text-[10px] font-semibold text-slate-500 bg-white dark:bg-slate-900 px-2 py-0.5 rounded border border-slate-200 dark:border-slate-800">
                                  {perms.length} izin
                                </span>
                              </div>
                              <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {perms.map((perm) => {
                                  const action = getActionBadge(perm)
                                  return (
                                    <div
                                      key={perm}
                                      className="flex items-center justify-between gap-2 p-2 rounded-lg bg-white dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800/80"
                                    >
                                      <span className="font-mono text-[11px] text-slate-700 dark:text-slate-300 truncate" title={perm}>
                                        {perm}
                                      </span>
                                      <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold ${action.badgeClass}`}>
                                        {action.label}
                                      </span>
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-xs text-slate-400 dark:border-slate-700">
                          {permissionSearch
                            ? `Tidak ada hak akses yang cocok dengan "${permissionSearch}".`
                            : 'Belum ada hak akses yang tercatat.'}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Footer */}
                  <footer className="erp-modal-footer shrink-0 flex items-center justify-between bg-white dark:bg-slate-950 border-t border-slate-200 dark:border-slate-800 p-4">
                    <span className="text-xs text-slate-500">
                      Total: <strong className="text-teal-600 dark:text-teal-400">{user?.permissions.length ?? 0}</strong> hak akses aktif
                      {permissionSearch && ` (${totalFilteredCount} ditampilkan)`}
                    </span>
                    <Button data-testid="shell-user-menu-close-btn"
                      variant="secondary"
                      onPress={() => {
                        setOpenDialog(null)
                        setPermissionSearch('')
                      }}
                    >
                      Tutup
                    </Button>
                  </footer>
                </>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}
